import { useEffect, useState } from "react";
import {
  Box, Typography, CircularProgress, Tooltip, IconButton, Dialog, MenuItem, Select,
} from "@mui/material";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import strings from "../../i18n/language";
import { instance } from "../../axios";

const BRAND = '#3b95e8';
const GLASS = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(232,240,254,0.38)';
const ERROR = '#ff7b7b';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const card = {
  background: GLASS,
  border: `1px solid ${BORDER}`,
  borderRadius: 20,
  backdropFilter: 'blur(12px)',
  overflow: 'hidden',
};

const inputStyle = {
  width: '100%', padding: '12px 14px', background: 'rgba(255,255,255,0.06)',
  border: `1px solid ${BORDER}`, borderRadius: 10, color: '#e8f0fe', fontSize: 14,
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: MUTED, margin: '14px 0 6px' };

const INVITATION_META = {
  accepted: { color: '#34c38f', bg: 'rgba(52,195,143,0.12)', label: () => strings.LABEL_ACCOUNTS_INVITATION_ACCEPTED },
  pending: { color: '#f5b544', bg: 'rgba(245,181,68,0.12)', label: () => strings.LABEL_ACCOUNTS_INVITATION_PENDING },
  expired: { color: ERROR, bg: 'rgba(255,123,123,0.12)', label: () => strings.LABEL_ACCOUNTS_INVITATION_EXPIRED },
};

const Pill = ({ color, bg, children }) => (
  <span style={{ background: bg, color, borderRadius: 6, padding: '3px 9px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
    {children}
  </span>
);

const Th = ({ children, style }) => (
  <th style={{
    textAlign: 'left', fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase',
    letterSpacing: 0.6, padding: '14px 16px', borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap', ...style,
  }}>
    {children}
  </th>
);

const Td = ({ children, style }) => (
  <td style={{
    padding: '12px 16px', fontSize: 13, color: 'rgba(232,240,254,0.75)',
    borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'middle', ...style,
  }}>
    {children}
  </td>
);

const Action = ({ title, onClick, hoverColor = BRAND, children }) => (
  <Tooltip title={title} placement="top">
    <IconButton size="small" onClick={onClick}
      sx={{ color: 'rgba(232,240,254,0.4)', '&:hover': { color: hoverColor, background: 'rgba(255,255,255,0.06)' } }}>
      {children}
    </IconButton>
  </Tooltip>
);

const DialogButton = ({ primary, danger, disabled, onClick, children }) => (
  <button onClick={onClick} disabled={disabled} style={{
    padding: '9px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
    border: primary || danger ? 'none' : `1px solid ${BORDER}`,
    background: danger ? '#e05c5c' : primary ? BRAND : 'rgba(255,255,255,0.05)',
    color: primary || danger ? '#fff' : MUTED, opacity: disabled ? 0.5 : 1,
  }}>
    {children}
  </button>
);

const errorText = (error) => {
  const code = error?.response?.data?.error;
  return strings[`LABEL_ACCOUNTS_ERROR_${code}`] || strings.LABEL_ACCOUNTS_ERROR;
};

// Create (no account) or edit (an account): email, name and company
const AccountForm = ({ open, account, companies, onClose, onSaved }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmail(account?.id || '');
    setName(account?.displayName || '');
    setCompanyId(account?.company?.id || '');
    setSaving(false);
  }, [open, account]);

  const emailInvalid = !account && email && !EMAIL_PATTERN.test(email.trim());
  const canSave = !saving && name.trim() && companyId && (account || EMAIL_PATTERN.test(email.trim()));

  const save = async () => {
    setSaving(true);
    try {
      if (account) {
        const response = await instance.post(`/account/${encodeURIComponent(account.id)}`, { displayName: name, company_id: companyId });
        onSaved(response.data);
        toast.success(strings.LABEL_ACCOUNTS_SAVED);
      } else {
        const response = await instance.post('/account', { email, displayName: name, company_id: companyId });
        onSaved(response.data);
        if (response.data.invitationError) toast.error(strings.LABEL_ACCOUNTS_ERROR_INVITATION_NOT_SENT);
        else toast.success(strings.formatString(strings.LABEL_ACCOUNTS_CREATED, response.data.id));
      }
      onClose();
    } catch (error) {
      setSaving(false);
      toast.error(errorText(error));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs"
      PaperProps={{ sx: { background: '#0d1b36', border: `1px solid ${BORDER}`, borderRadius: 4, p: 3 } }}>
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#e8f0fe' }}>
        {account ? strings.LABEL_ACCOUNTS_EDIT : strings.LABEL_ACCOUNTS_NEW}
      </Typography>
      {!account && (
        <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.5 }}>{strings.LABEL_ACCOUNTS_NEW_HINT}</Typography>
      )}

      <label style={labelStyle}>{strings.LABEL_ACCOUNTS_EMAIL}</label>
      <input type="email" value={email} disabled={Boolean(account)} autoFocus={!account}
        onChange={(e) => setEmail(e.target.value)}
        style={{ ...inputStyle, borderColor: emailInvalid ? ERROR : BORDER, opacity: account ? 0.6 : 1 }} />

      <label style={labelStyle}>{strings.LABEL_ACCOUNTS_NAME}</label>
      <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />

      <label style={labelStyle}>{strings.LABEL_ACCOUNTS_COMPANY}</label>
      <Select value={companyId} onChange={(e) => setCompanyId(e.target.value)} displayEmpty fullWidth size="small"
        sx={{ color: '#e8f0fe', background: 'rgba(255,255,255,0.06)', borderRadius: '10px', fontSize: 14,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER }, '& .MuiSvgIcon-root': { color: MUTED } }}>
        <MenuItem value="" disabled>{strings.LABEL_ACCOUNTS_CHOOSE_COMPANY}</MenuItem>
        {companies.map((company) => (
          <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
        ))}
      </Select>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <DialogButton onClick={onClose}>{strings.LABEL_ACCOUNTS_CANCEL}</DialogButton>
        <DialogButton primary disabled={!canSave} onClick={save}>
          {account ? strings.LABEL_ACCOUNTS_SAVE : strings.LABEL_ACCOUNTS_CREATE}
        </DialogButton>
      </Box>
    </Dialog>
  );
};

const AccountList = () => {
  const [accounts, setAccounts] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState(false);
  const [form, setForm] = useState(null); // { account } while the form is open
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    instance.get('/account').then((response) => setAccounts(response.data)).catch(() => setError(true));
    instance.get('/company').then((response) => {
      const list = Array.isArray(response.data) ? response.data : response.data.data || [];
      setCompanies(list.map((c) => ({ id: c.id, name: c.name.trim() })).sort((a, b) => a.name.localeCompare(b.name)));
    }).catch(() => {});
  }, []);

  const replace = (account) => setAccounts((list) => {
    const others = (list || []).filter((a) => a.id !== account.id);
    return [account, ...others].sort((a, b) => Number(a.admin) - Number(b.admin) || (a.company?.name || '').localeCompare(b.company?.name || ''));
  });

  const invite = async (account) => {
    try {
      const response = await instance.post(`/account/${encodeURIComponent(account.id)}/invite`);
      replace(response.data);
      toast.success(strings.formatString(strings.LABEL_ACCOUNTS_INVITATION_SENT, account.id));
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  const toggleStatus = async (account) => {
    try {
      const proStatus = account.proStatus === 'active' ? 'waiting' : 'active';
      const response = await instance.post(`/account/${encodeURIComponent(account.id)}`, { proStatus });
      replace(response.data);
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  const remove = async () => {
    const account = toDelete;
    setToDelete(null);
    try {
      await instance.delete(`/account/${encodeURIComponent(account.id)}`);
      setAccounts((list) => list.filter((a) => a.id !== account.id));
      toast.success(strings.formatString(strings.LABEL_ACCOUNTS_DELETED, account.id));
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: `1px solid ${BORDER}` }}>
          <div>
            <Typography style={{ fontSize: 15, fontWeight: 700, color: '#e8f0fe', letterSpacing: 0.2 }}>
              {strings.LABEL_ACCOUNTS_TITLE}
            </Typography>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>{strings.LABEL_ACCOUNTS_SUBTITLE}</div>
          </div>
          <button onClick={() => setForm({ account: null })} style={{
            display: 'flex', alignItems: 'center', gap: 6, background: BRAND, color: '#fff', border: 'none',
            borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(59,149,232,0.3)',
          }}>
            <AddRoundedIcon style={{ fontSize: 16 }} /> {strings.LABEL_ACCOUNTS_NEW}
          </button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <Th>{strings.LABEL_ACCOUNTS_ACCOUNT}</Th>
                <Th>{strings.LABEL_ACCOUNTS_COMPANY}</Th>
                <Th>{strings.LABEL_ACCOUNTS_STATUS}</Th>
                <Th>{strings.LABEL_ACCOUNTS_INVITATION}</Th>
                <Th>{strings.LABEL_ACCOUNTS_LAST_SEEN}</Th>
                <Th style={{ width: 150 }} />
              </tr>
            </thead>
            <tbody>
              {!accounts && !error && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32 }}><CircularProgress size={22} sx={{ color: BRAND }} /></td></tr>
              )}
              {error && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, fontSize: 13, color: ERROR }}>{strings.LABEL_ACCOUNTS_ERROR}</td></tr>
              )}
              {accounts && accounts.map((account) => {
                const invitation = INVITATION_META[account.invitation];
                return (
                  <tr key={account.id}>
                    <Td>
                      <div style={{ color: '#e8f0fe', fontWeight: 500 }}>
                        {account.displayName || '—'}
                        {account.admin && <span style={{ marginLeft: 6, fontSize: 10, color: BRAND, fontWeight: 700 }}>ADMIN</span>}
                      </div>
                      <div style={{ fontSize: 11, color: MUTED }}>{account.id}</div>
                    </Td>
                    <Td style={{ color: MUTED }}>{account.admin ? '—' : account.company?.name || '—'}</Td>
                    <Td>
                      {account.admin ? '—' : account.proStatus === 'active'
                        ? <Pill color="#34c38f" bg="rgba(52,195,143,0.12)">{strings.LABEL_ACCOUNTS_ACTIVE}</Pill>
                        : <Pill color={MUTED} bg="rgba(255,255,255,0.06)">{strings.LABEL_ACCOUNTS_SUSPENDED}</Pill>}
                    </Td>
                    <Td>
                      {invitation && (
                        <Tooltip placement="top" title={account.invitation === 'pending' && account.invitationExpires
                          ? strings.formatString(strings.LABEL_ACCOUNTS_INVITATION_UNTIL, dayjs(account.invitationExpires).format('LLL')) : ''}>
                          <span><Pill color={invitation.color} bg={invitation.bg}>{invitation.label()}</Pill></span>
                        </Tooltip>
                      )}
                    </Td>
                    <Td style={{ color: account.lastSeen ? '#e8f0fe' : MUTED, whiteSpace: 'nowrap', fontSize: 12 }}>
                      {account.lastSeen ? dayjs(account.lastSeen).format('LLL') : strings.LABEL_ACCOUNTS_NEVER}
                    </Td>
                    <Td>
                      {!account.admin && (
                        <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end' }}>
                          {account.invitation !== 'accepted' && (
                            <Action title={strings.LABEL_ACCOUNTS_RESEND} onClick={() => invite(account)}>
                              <SendRoundedIcon sx={{ fontSize: 16 }} />
                            </Action>
                          )}
                          <Action title={strings.LABEL_ACCOUNTS_EDIT} onClick={() => setForm({ account })} hoverColor="#e8f0fe">
                            <EditRoundedIcon sx={{ fontSize: 16 }} />
                          </Action>
                          <Action title={account.proStatus === 'active' ? strings.LABEL_ACCOUNTS_SUSPEND : strings.LABEL_ACCOUNTS_REACTIVATE}
                            onClick={() => toggleStatus(account)} hoverColor="#f5b544">
                            {account.proStatus === 'active'
                              ? <PauseCircleOutlineRoundedIcon sx={{ fontSize: 17 }} />
                              : <PlayCircleOutlineRoundedIcon sx={{ fontSize: 17 }} />}
                          </Action>
                          <Action title={strings.LABEL_ACCOUNTS_DELETE} onClick={() => setToDelete(account)} hoverColor="#e05c5c">
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />
                          </Action>
                        </Box>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <AccountForm
        open={Boolean(form)}
        account={form?.account || null}
        companies={companies}
        onClose={() => setForm(null)}
        onSaved={replace}
      />

      <Dialog open={Boolean(toDelete)} onClose={() => setToDelete(null)} maxWidth="xs"
        PaperProps={{ sx: { background: '#0d1b36', border: `1px solid ${BORDER}`, borderRadius: 4, p: 3 } }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#e8f0fe' }}>{strings.LABEL_ACCOUNTS_DELETE}</Typography>
        <Typography sx={{ fontSize: 13, color: MUTED, mt: 1 }}>
          {toDelete && strings.formatString(strings.LABEL_ACCOUNTS_DELETE_CONFIRM, toDelete.id)}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
          <DialogButton onClick={() => setToDelete(null)}>{strings.LABEL_ACCOUNTS_CANCEL}</DialogButton>
          <DialogButton danger onClick={remove}>{strings.LABEL_ACCOUNTS_DELETE}</DialogButton>
        </Box>
      </Dialog>
    </Box>
  );
};

export default AccountList;
